import { get, post, put } from '@/api/apiService.ts';
import apiClient from '@/api/apiClient.ts';
import type {
  AuthResponse,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  LoginRequest,
  RegisterRequest,
  ResetPasswordRequest,
  UpdateProfileRequest,
  User,
} from '@/types/auth.ts';

const authService = {
  register(data: RegisterRequest): Promise<AuthResponse> {
    return post<RegisterRequest, AuthResponse>('/api/auth/register', data);
  },

  login(data: LoginRequest): Promise<AuthResponse> {
    return post<LoginRequest, AuthResponse>('/api/auth/login', data);
  },

  refresh(refreshToken: string): Promise<{ accessToken: string; refreshToken: string }> {
    return post('/api/auth/refresh', { refreshToken });
  },

  logout(refreshToken: string | null): Promise<void> {
    // El backend revoca el refresh token (si llega) y resetea el flujo elegido
    // (MVP) para que el usuario pueda volver a elegir en el próximo login.
    return post<{ refreshToken: string | null }, void>('/api/auth/logout', { refreshToken });
  },

  getMe(): Promise<User> {
    return get<User>('/api/auth/me');
  },

  updateProfile(data: UpdateProfileRequest): Promise<User> {
    return put<UpdateProfileRequest, User>('/api/auth/me', data);
  },

  async uploadAvatar(file: Blob): Promise<User> {
    const formData = new FormData();
    formData.append('file', file, 'avatar.jpg');
    const { data } = await apiClient.post<User>('/api/auth/me/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  async deleteAvatar(): Promise<User> {
    const { data } = await apiClient.delete<User>('/api/auth/me/avatar');
    return data;
  },

  changePassword(data: ChangePasswordRequest): Promise<void> {
    return put<ChangePasswordRequest, void>('/api/auth/me/password', data);
  },

  forgotPassword(data: ForgotPasswordRequest): Promise<void> {
    return post<ForgotPasswordRequest, void>('/api/auth/forgot-password', data);
  },

  resetPassword(data: ResetPasswordRequest): Promise<void> {
    return post<ResetPasswordRequest, void>('/api/auth/reset-password', data);
  },

  verifyEmail(token: string): Promise<User> {
    return post<{ token: string }, User>('/api/auth/verify-email', { token });
  },

  resendVerification(): Promise<void> {
    return post<Record<string, never>, void>('/api/auth/resend-verification', {});
  },

  acknowledgePrivacy(): Promise<User> {
    return post<Record<string, never>, User>('/api/auth/me/acknowledge-privacy', {});
  },

  setChosenFlow(flow: 'evaluate' | 'pitch'): Promise<User> {
    return put<{ flow: string }, User>('/api/auth/me/flow', { flow });
  },

  exportMyData(): Promise<unknown> {
    return get<unknown>('/api/auth/me/export');
  },
};

export default authService;
